import { useConfiguratorStateDomain } from "@/configurator/domain/hooks/useConfiguratorStateDomain";
import { buildARShareUrl } from "@/configurator/domain/logic/arShareLink";
import { findProduct } from "@/configurator/domain/logic/catalog";
import { buildOfferGroups } from "@/configurator/domain/logic/offer";
import {
  buildOfferFileName,
  buildOfferRenderInput,
} from "@/configurator/domain/logic/offerDocument";
import { getTotalPrice } from "@/configurator/domain/logic/pricing";
import { buildSharedSelection } from "@/configurator/domain/logic/sharing";
import type { ConfiguratorSnapshot } from "@/configurator/domain/ports/configuratorState";
import { saveBlob } from "@/configurator/utils/download";

// Module scope keeps the identity stable — see the note in useConfiguratorDomain.
const selectActiveProduct = (s: ConfiguratorSnapshot) =>
  findProduct(s.config.catalog, s.activeProductId);

const selectBrand = (s: ConfiguratorSnapshot) => s.config.brand;

const selectQuantity = (s: ConfiguratorSnapshot) => s.quantity;

const selectOfferGroups = (s: ConfiguratorSnapshot) =>
  buildOfferGroups(
    findProduct(s.config.catalog, s.activeProductId),
    s.selectedOptions,
  );

const selectTotal = (s: ConfiguratorSnapshot) =>
  getTotalPrice(
    findProduct(s.config.catalog, s.activeProductId),
    s.selectedOptions,
    s.quantity,
  );

const selectSharedSelection = (s: ConfiguratorSnapshot) =>
  buildSharedSelection(
    s.config,
    s.activeProductId,
    s.selectedModelId,
    s.selectedOptions,
  );

const FILE_NAME_FALLBACK = "konfigurace";

export type OfferRequest = {
  /** The page the printed QR code should send a phone back to. */
  pageUrl: string;
  /** PNG data URL of the 3D view, when the scene had one to give. */
  screenshotDataUrl?: string;
};

export type OfferDomain = {
  /** True once there is a product to write an offer about. */
  available: boolean;
  downloadOffer: (request: OfferRequest) => Promise<void>;
};

export function useOfferDomain(): OfferDomain {
  const { useSelect } = useConfiguratorStateDomain();
  const product = useSelect(selectActiveProduct);
  const brand = useSelect(selectBrand);
  const groups = useSelect(selectOfferGroups);
  const quantity = useSelect(selectQuantity);
  const total = useSelect(selectTotal);
  const selection = useSelect(selectSharedSelection);

  const downloadOffer = async (request: OfferRequest) => {
    if (!product) return;

    // Imported here rather than at module scope so the demo build keeps jsPDF
    // and the embedded Exo 2 subsets out of its initial chunk; the widget's
    // IIFE build cannot code-split and inlines them either way.
    const { renderOfferPdf, slugify } =
      await import("@/configurator/utils/pdf/renderOfferPdf");

    const date = new Date();
    const blob = await renderOfferPdf(
      buildOfferRenderInput(
        {
          title: product.title,
          arShareUrl: buildARShareUrl(request.pageUrl, selection),
          brand,
          groups,
          quantity,
          total,
          date,
        },
        request.screenshotDataUrl,
      ),
    );

    saveBlob(
      blob,
      buildOfferFileName(slugify(product.title, FILE_NAME_FALLBACK), date),
    );
  };

  return { available: Boolean(product), downloadOffer };
}
